/**
 * The width profile: how wide the face is at every height, from the superior pole to the menton.
 *
 * This is the measurement the whole classifier rests on, and it is resampled rather than read off the ring directly. The ring's own vertical spacing is wildly uneven — in the canonical mesh the gap between consecutive ring points varies by a factor of about 75, with the top of the forehead densely sampled and the mid-face sparse — so treating ring index as a proxy for height would stretch some parts of the face and compress others. Sampling at even heights costs one interpolation and removes the whole class of error.
 */
import { MENTON_INDEX, SUPERIOR_INDEX, type Outline, type Point } from './oval';

/** Heights sampled between the poles. Odd, so one sample lands exactly at the middle. */
export const PROFILE_SAMPLES = 33;

export interface WidthProfile {
  /** Sample heights, 0 at the superior pole and 1 at the menton. */
  t: readonly number[];
  /** Full width at each height, pixels. */
  width: readonly number[];
  /** Each side's distance from the midline at each height, pixels. */
  left: readonly number[];
  right: readonly number[];
  /** Pole-to-pole height, pixels. */
  lengthPx: number;
  /** The widest chord and where it sits. */
  maxWidthPx: number;
  widestAt: number;
}

/** One side of the outline as a chain of points from the superior pole to the menton. */
interface Chain {
  points: Point[];
  /** False when the chain doubles back in y, which means the outline is not a face seen near-frontally. */
  monotonic: boolean;
}

function chains(outline: Outline): { left: Chain; right: Chain } {
  const top = outline[SUPERIOR_INDEX]!;
  const chin = outline[MENTON_INDEX]!;

  // Ring positions 1..17 are one side and 19..35 the other; which is which depends on the image, so it is decided by where they actually lie rather than assumed.
  const first: Point[] = [];
  const second: Point[] = [];
  for (let i = 1; i < MENTON_INDEX; i += 1) first.push(outline[i]!);
  for (let i = outline.length - 1; i > MENTON_INDEX; i -= 1) second.push(outline[i]!);

  const meanX = (ps: Point[]) => ps.reduce((s, p) => s + p.x, 0) / ps.length;
  const [leftSide, rightSide] = meanX(first) < meanX(second) ? [first, second] : [second, first];

  const build = (side: Point[]): Chain => {
    const points = [top, ...side, chin];
    let monotonic = true;
    for (let i = 1; i < points.length; i += 1) {
      if (points[i]!.y < points[i - 1]!.y) monotonic = false;
    }
    return { points, monotonic };
  };

  return { left: build(leftSide), right: build(rightSide) };
}

/** Where a chain sits horizontally at a given height, by linear interpolation between its points. */
function xAt(chain: Chain, y: number): number {
  const ps = chain.points;
  if (y <= ps[0]!.y) return ps[0]!.x;
  for (let i = 1; i < ps.length; i += 1) {
    const a = ps[i - 1]!;
    const b = ps[i]!;
    if (y <= b.y) {
      const span = b.y - a.y;
      const f = span === 0 ? 0 : (y - a.y) / span;
      return a.x + (b.x - a.x) * f;
    }
  }
  return ps[ps.length - 1]!.x;
}

export interface ProfileResult {
  profile: WidthProfile;
  /** False when either side doubled back in y; the outline is then not measurable as a profile. */
  measurable: boolean;
}

/**
 * The profile of an already-normalised outline, measured from its fitted midline.
 *
 * `midX` matters: the two half-widths are what the symmetry gate compares, and taken from each chord's own midpoint they would be equal on every face, gate included.
 */
export function widthProfile(
  outline: Outline,
  midX: number,
  samples = PROFILE_SAMPLES,
): ProfileResult {
  const { left, right } = chains(outline);
  const yTop = outline[SUPERIOR_INDEX]!.y;
  const yChin = outline[MENTON_INDEX]!.y;
  const lengthPx = yChin - yTop;

  const t: number[] = [];
  const width: number[] = [];
  const leftDist: number[] = [];
  const rightDist: number[] = [];

  for (let i = 0; i < samples; i += 1) {
    const ti = i / (samples - 1);
    const y = yTop + ti * lengthPx;
    const xl = xAt(left, y);
    const xr = xAt(right, y);
    t.push(ti);
    width.push(xr - xl);
    leftDist.push(midX - xl);
    rightDist.push(xr - midX);
  }

  let maxWidthPx = 0;
  let widestAt = 0;
  for (let i = 0; i < width.length; i += 1) {
    if (width[i]! > maxWidthPx) {
      maxWidthPx = width[i]!;
      widestAt = t[i]!;
    }
  }

  return {
    profile: { t, width, left: leftDist, right: rightDist, lengthPx, maxWidthPx, widestAt },
    measurable: left.monotonic && right.monotonic && lengthPx > 0 && maxWidthPx > 0,
  };
}

/** Mean width over a band of heights, which is steadier than any single sample. */
export function bandWidth(profile: WidthProfile, from: number, to: number): number {
  let sum = 0;
  let n = 0;
  for (let i = 0; i < profile.t.length; i += 1) {
    const ti = profile.t[i]!;
    if (ti >= from && ti <= to) {
      sum += profile.width[i]!;
      n += 1;
    }
  }
  return n === 0 ? 0 : sum / n;
}

/** The profile's width at one height, interpolated between samples. */
export function widthAt(profile: WidthProfile, t: number): number {
  const last = profile.t.length - 1;
  const pos = Math.max(0, Math.min(last, t * last));
  const i = Math.floor(pos);
  const f = pos - i;
  const a = profile.width[i]!;
  const b = profile.width[Math.min(last, i + 1)]!;
  return a + (b - a) * f;
}
